import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { VisibilityOutlined, VisibilityOffOutlined, HourglassEmptyOutlined } from "@mui/icons-material";
import { SubmitHandler, useForm, Controller } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import AvatarIcon from "../../shared/assets/icons/AvatarIcon";
import { refreshAccount } from "../accountSlice";
import FormNotificationMessage from "../../shared/components/FormNotificationMessage";
import { passwordRegex } from "../../shared/validators";
import Loader from "../../shared/components/Loader";
import { useAppDispatch, useAppSelector } from "../../shared/hooks/sharedHooks";
import { ConfirmRegisterForm } from "../models/AccountModels";
import { ConfirmRegister, CustomerTypeCode } from "../../customer/shared/models/customerModels";
import SecuredConnection from "./SecuredConnection";
import colors from "../../shared/themes/colors";
import MachineLogoIcon from "../../shared/assets/icons/MachineLogoIcon";
import {
  loadCountries,
  confirmRegistration,
  togglePasswordVisibility,
  toggleConfirmPasswordVisibility,
  resetState,
  setFormNotificationMessage,
} from "../confirmRegistrationSlice";
import { handleLogin } from "../loginSlice";
import { NotificationMessage } from "../../shared/models/models";
import useTranslation from "../../shared/hooks/useTranslation";
import useDefaultRedirect from "../hooks/useDefaultRedirect";
import { PARTNER_INVITE_STORAGE_KEY } from "../routes";
import { dt } from "../../shared/themes/redesignTokens";

const STEP_COMPANY = 1;
const STEP_PERSONAL = 2;

const ConfirmRegistration = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { translate } = useTranslation("account");
  const { translate: tCommon } = useTranslation("common");
  const { defaultRoute } = useDefaultRedirect();

  const [step, setStep] = useState(STEP_COMPANY);
  const [pendingApproval, setPendingApproval] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    watch,
    control,
    setValue,
    trigger,
    formState: { errors }
  } = useForm<ConfirmRegisterForm>({
    defaultValues: {
      customerType: CustomerTypeCode.MachineOperator,
    }
  });
  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    isLoading,
    formNotificationMessage,
    showPassword,
    showConfirmPassword,
    countries,
  } = useAppSelector((state) => state.confirmRegistration);
  const { user } = useAppSelector((state) => state.account);
  const { isLoading: isLoadingLogin } = useAppSelector((state) => state.login);

  const watchedCustomerType = watch('customerType');
  const watchedName = watch('name');
  const watchedCustomIndustryName = watch('customIndustryName');
  const watchedCountryId = watch('countryId');

  const isStep1Valid = !!watchedName && watchedName.trim() !== ''
    && !!watchedCustomIndustryName && watchedCustomIndustryName.trim() !== ''
    && !!watchedCountryId
    && watchedCustomerType > 0;

  const watchedFirstName = watch('firstName');
  const watchedLastName = watch('lastName');
  const watchedPassword = watch('password');
  const watchedConfirmPassword = watch('confirmPassword');

  const isStep2Valid = !!watchedFirstName && watchedFirstName.trim() !== ''
    && !!watchedLastName && watchedLastName.trim() !== ''
    && !!watchedPassword && watchedPassword.trim() !== ''
    && !!watchedConfirmPassword && watchedConfirmPassword.trim() !== '';

  const handleClickShowPassword = () => {
    dispatch(togglePasswordVisibility());
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleClickShowConfirmPassword = () => {
    dispatch(toggleConfirmPasswordVisibility());
  };

  const handleMouseDownConfirmPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleConfirmRegistration = async (model: ConfirmRegisterForm) => {
    const { confirmPassword, phoneNumber, department, ...restModel } = model;

    // Check for partner invite token stored during sign-up
    const partnerInviteToken = sessionStorage.getItem(PARTNER_INVITE_STORAGE_KEY) || undefined;

    const registerModel: ConfirmRegister = {
      ...restModel,
      partnerInviteToken,
    };

    try {
      const result = await dispatch(confirmRegistration(registerModel)).unwrap();

      // Clear the partner invite token after successful registration
      sessionStorage.removeItem(PARTNER_INVITE_STORAGE_KEY);

      // Account requires admin approval — show pending message instead of auto-login
      if (result.isPendingApproval) {
        setPendingApproval(true);
        return;
      }

      await dispatch(handleLogin({
        userName: result.userName,
        password: model.password
      })).unwrap();

      dispatch(refreshAccount(null));

      // If registration was via partner invite, redirect to partnerships page
      if (partnerInviteToken) {
        navigate("/partnerships", { replace: true });
      } else {
        navigate(defaultRoute(), { replace: true });
      }
    } catch (error: any) {
      dispatch(setFormNotificationMessage(NotificationMessage.error(error)));
    }
  };

  const onSubmit: SubmitHandler<ConfirmRegisterForm> = (model: ConfirmRegisterForm) => {
    handleConfirmRegistration(model);
  };

  const consumeUrl = () => {
    const paramsString = search.replace("?", "").replaceAll("+", "%2B");
    const params = new URLSearchParams(paramsString);

    const formValues = {
      userId: params.get("userId") || "",
      token: params.get("token") || "",
      customerType: CustomerTypeCode.MachineOperator,
    };
    reset(formValues);
  };

  const validateConfirmPassword = (value?: string) => {
    const { password } = getValues();
    return password === value || translate("passwordsDoNotMatch");
  };

  const handleNextStep = async () => {
    const valid = await trigger(['name', 'customIndustryName', 'countryId']);
    if (valid && isStep1Valid) {
      setStep(STEP_PERSONAL);
    }
  };

  const handleBackStep = () => {
    setStep(STEP_COMPANY);
  };

  useEffect(() => {
    consumeUrl();
    dispatch(loadCountries());

    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  const renderStepIndicator = () => (
    <Box sx={{ display: "flex", justifyContent: "center", gap: "8px", mb: 3 }}>
      <Box sx={{
        width: "80px", height: "4px", borderRadius: "2px",
        backgroundColor: dt.blue,
      }} />
      <Box sx={{
        width: "80px", height: "4px", borderRadius: "2px",
        backgroundColor: step === STEP_PERSONAL ? dt.blue : colors.secondary.gray90,
      }} />
    </Box>
  );

  const renderCompanyStep = () => (
    <>
      <Box className="text-16-medium" sx={{ color: dt.blue, mb: 2 }}>
        {translate("registration.companySetup")}
      </Box>

      <Box className="flex flex-col pt-3">
        <InputLabel required>{translate("registration.companyType")}</InputLabel>
        <Controller
          name="customerType"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <ToggleButtonGroup
              value={field.value}
              exclusive
              onChange={(_e, newVal) => {
                if (newVal !== null) {
                  field.onChange(newVal);
                }
              }}
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: "8px",
                mt: 1,
                "& .MuiToggleButton-root": {
                  flex: 1,
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "14px",
                  borderRadius: "8px !important",
                  border: `1px solid ${colors.secondary.gray90} !important`,
                  color: colors.secondary.gray40,
                  "&.Mui-selected": {
                    backgroundColor: dt.blue,
                    color: "#fff",
                    borderColor: `${dt.blue} !important`,
                    "&:hover": {
                      backgroundColor: dt.blue,
                    }
                  },
                }
              }}
            >
              <ToggleButton value={CustomerTypeCode.MachineOperator}>
                {translate("registration.typeOperator")}
              </ToggleButton>
              <ToggleButton value={CustomerTypeCode.ServiceProvider}>
                {translate("registration.typeProvider")}
              </ToggleButton>
              <ToggleButton value={CustomerTypeCode.Both}>
                {translate("registration.typeBoth")}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        />
      </Box>

      <Box className="flex flex-col pt-6">
        <InputLabel required>{translate("companyName")}</InputLabel>
        <TextField
          type="text"
          variant="outlined"
          className="w-full"
          placeholder={translate("companyNamePlaceholder")}
          {...register("name", { required: true })}
          error={!!errors.name}
          helperText={errors.name ? tCommon("forms.required") : ""}
        />
      </Box>

      <Box className="flex flex-col pt-6">
        <InputLabel required>{translate("fieldOfBusiness")}</InputLabel>
        <TextField
          type="text"
          variant="outlined"
          className="w-full"
          placeholder={translate("industryPlaceholder")}
          {...register("customIndustryName", { required: true })}
          error={!!errors.customIndustryName}
          helperText={errors.customIndustryName ? tCommon("forms.required") : ""}
        />
      </Box>

      <Box className="flex flex-col pt-6">
        <InputLabel required>{translate("selectCountry")}</InputLabel>
        <Controller
          name="countryId"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Autocomplete
              {...field}
              options={countries}
              autoHighlight
              getOptionLabel={(option) => option.name}
              value={countries.find(country => country.id === field.value) || null}
              onChange={(_event, newValue) => {
                field.onChange(newValue ? newValue.id : '');
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  {option.name}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={translate("countryPlaceholder")}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password'
                  }}
                  error={!!errors.countryId}
                  helperText={errors.countryId ? tCommon("forms.required") : ""}
                />
              )}
            />
          )}
        />
      </Box>
    </>
  );

  const renderPersonalStep = () => (
    <>
      <Box className="text-16-medium" sx={{ color: dt.blue, mb: 2 }}>
        {translate("registration.personalAccount")}
      </Box>

      <Box className="flex flex-col pt-3">
        <InputLabel required>{translate("firstName")}</InputLabel>
        <TextField
          type="text"
          variant="outlined"
          className="w-full"
          placeholder={translate("firstNamePlaceholder")}
          {...register("firstName", { required: true })}
          error={!!errors.firstName}
          helperText={errors.firstName ? tCommon("forms.required") : ""}
        />
      </Box>

      <Box className="flex flex-col pt-6">
        <InputLabel required>{translate("lastName")}</InputLabel>
        <TextField
          type="text"
          variant="outlined"
          className="w-full"
          placeholder={translate("lastNamePlaceholder")}
          {...register("lastName", { required: true })}
          error={!!errors.lastName}
          helperText={errors.lastName ? tCommon("forms.required") : ""}
        />
      </Box>

      <Box className="flex flex-col pt-6">
        <InputLabel required>{translate("password")}</InputLabel>
        <TextField
          type={showPassword ? "text" : "password"}
          variant="outlined"
          className="w-full"
          placeholder=""
          inputProps={{ autoComplete: 'new-password' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                >
                  {showPassword ? <VisibilityOutlined fontSize="small" /> : <VisibilityOffOutlined fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )
          }}
          {...register("password", {
            required: tCommon("forms.required"),
            pattern: { value: passwordRegex, message: translate("passwordRequirements") }
          })}
          error={!!errors.password}
          helperText={errors.password ? errors.password.message || translate("fieldInvalid") : ""}
        />
      </Box>

      <Box className="flex flex-col pt-6">
        <InputLabel required>{translate("repeatPassword")}</InputLabel>
        <TextField
          type={showConfirmPassword ? "text" : "password"}
          variant="outlined"
          className="w-full"
          placeholder=""
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowConfirmPassword}
                  onMouseDown={handleMouseDownConfirmPassword}
                >
                  {showConfirmPassword ? <VisibilityOutlined fontSize="small" /> : <VisibilityOffOutlined fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )
          }}
          {...register("confirmPassword", { validate: validateConfirmPassword })}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword ? errors.confirmPassword.message || translate("fieldInvalid") : ""}
        />
      </Box>

      <Box className="flex flex-col pt-6">
        <InputLabel>{translate("department")}</InputLabel>
        <TextField
          type="text"
          variant="outlined"
          className="w-full"
          placeholder={translate("departmentPlaceholder")}
          {...register("department")}
        />
      </Box>

      <Box className="flex flex-col pt-6">
        <InputLabel>{translate("phoneNumber")}</InputLabel>
        <TextField
          type="text"
          variant="outlined"
          className="w-full"
          placeholder={translate("phoneNumberPlaceholder")}
          {...register("phoneNumber")}
        />
      </Box>
    </>
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: colors.secondary.gray98,
      }}
    >
      <Card
        sx={{
          maxWidth: "600px",
          width: "100%",
          margin: "16px",
          display: "flex",
          flexDirection: "column",
          borderRadius: "8px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          padding: isMobile ? "32px 16px 32px" : "64px 48px 64px",
        }}
      >
        {pendingApproval ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              py: 4,
              px: 2,
            }}
          >
            <MachineLogoIcon />
            <Box sx={{
              mt: 3,
              mb: 2,
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: '#FFF3E0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <HourglassEmptyOutlined sx={{ fontSize: 32, color: '#F57C00' }} />
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, mb: 2 }}
            >
              {translate("pendingApproval.title")}
            </Typography>
            <Typography
              sx={{
                color: colors.secondary.gray40,
                fontSize: '14px',
                lineHeight: '22px',
                mb: 3,
                maxWidth: '400px',
              }}
            >
              {translate("pendingApproval.description")}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/account/login", { replace: true })}
              sx={{ mt: 1 }}
            >
              {translate("backToLogin")}
            </Button>
            <Box className="w-full text-center" sx={{ mt: 3 }}>
              <SecuredConnection />
            </Box>
          </Box>
        ) : isLoading || isLoadingLogin || user ? (
          <Box
            sx={{
              width: '460px',
              minHeight: '420px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Loader />
          </Box>
        ) : (
          <>
            <CardHeader
              title={
                <Box className="flex flex-col items-center justify-start gap-4" sx={{ paddingTop: isMobile ? "16px" : "0" }}>
                  <MachineLogoIcon />
                  <Box
                    className={`flex ${isMobile ? 'flex-col' : 'flex-row'} items-center justify-center gap-4`}
                  >
                    {!isMobile && <AvatarIcon sx={{ width: 90, height: 96 }} />}
                    <Box
                      sx={{
                        fontSize: isMobile ? "18px" : "24px",
                        fontWeight: "500",
                        lineHeight: isMobile ? "24px" : "36px",
                        letterSpacing: "-0.048px",
                      }}>
                      {step === STEP_COMPANY
                        ? translate("registration.step2Title")
                        : translate("registration.step3Title")
                      }
                    </Box>
                  </Box>
                </Box>
              }
              sx={{ pt: 0 }}
            />

            <CardContent>
              {renderStepIndicator()}

              {formNotificationMessage && (
                <Box className="mb-3">
                  <FormNotificationMessage {...formNotificationMessage} />
                </Box>
              )}

              <Box component="form" id="confirmRegistrationForm" onSubmit={handleSubmit(onSubmit)}>
                {step === STEP_COMPANY && renderCompanyStep()}
                {step === STEP_PERSONAL && renderPersonalStep()}
              </Box>
            </CardContent>

            <CardActions sx={{
              padding: "24px 16px 20px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              pointerEvents: "none",
              "& button": {
                pointerEvents: "auto"
              },
              "& .MuiButton-root": {
                pointerEvents: "auto"
              }
            }}>
              {step === STEP_COMPANY && (
                <Button
                  variant="contained"
                  className="w-full"
                  disabled={!isStep1Valid}
                  onClick={handleNextStep}
                >
                  {translate("registration.nextStep")}
                </Button>
              )}

              {step === STEP_PERSONAL && (
                <>
                  <Button
                    form="confirmRegistrationForm"
                    type="submit"
                    variant="contained"
                    className="w-full"
                    disabled={isLoading || !isStep2Valid}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isLoading && isStep2Valid) {
                        handleSubmit(onSubmit)();
                      }
                    }}
                  >
                    {translate("createAccount")}
                  </Button>
                  <Button
                    variant="text"
                    className="w-full"
                    onClick={handleBackStep}
                    sx={{ color: colors.secondary.gray40 }}
                  >
                    {translate("registration.backStep")}
                  </Button>
                </>
              )}
            </CardActions>

            <Box className="w-full text-center">
              <SecuredConnection />
            </Box>
          </>
        )}
      </Card>
    </Box >
  );
};

export default ConfirmRegistration;
